<template>
  <b-card header="Weather Preview" style="margin-bottom: 0;">
    <b-card-body>
      <b-row>
        <b-col cols="3">
          <b-table striped hover bordered small :items="measurements" class="text-center"></b-table>
          <b-row class="text-center">
            <b-col>
              <b-form-group label="Time Options" >
                <b-form-radio-group id="timeOpt"
                  buttons
                  stacked
                  v-model="selectedTimeOpt"
                  :options="timeOpts"
                  name="timeOpt"/>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group label="Display Options">
                <b-form-radio-group id="displayOpt"
                  buttons
                  stacked
                  v-model="selectedOpt"
                  :options="options"
                  name="displayOpt"/>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
        <b-col cols="9">
          <highcharts :options="lineOptions"></highcharts>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';

const lineOptions = {
  title: {
    text: ''
  },
  chart: {
    zoomType: 'x'
  },
  xAxis: {
    type: 'datetime',
    gridLineColor: '#E0E0E0',
    gridLineWidth: 1,
  },
  yAxis: {
    title: {
      text: 'Irradiance [W/m^2]'
    },
    showEmpty: false
  },
  legend: {
    enabled: false,
    layout: 'horizontal',
    align: 'center',
    verticalAlign: 'bottom'
  },
  plotOptions: {
    series: {
      label: {
        connectorAllowed: false
      },
      marker: {
        enabled: false
      },
      pointStart: 5,
      animation: false,
      turboThreshold: 0
    }
  },
  series: [],
  responsive: {
    rules: [{
      condition: {
        maxWidth: 500
      },
      chartOptions: {
        legend: {
          layout: 'horizontal',
          align: 'center',
          verticalAlign: 'bottom'
        },
        yAxis: [
          { visible: false }
        ]
      }
    }]
  },
  credits: false,
  exporting: {
    sourceWidth: 1200,
    sourceHeight: 600
  }
};

const formatDatetime = (opt) => {
  const datetimeOutput = {
    startDate: null,
    endDate: null
  };

  if (opt === 'today') {
    datetimeOutput.startDate = moment(new Date()).format('YYYY-MM-DD 00:00:00');
    datetimeOutput.endDate = moment(new Date()).format('YYYY-MM-DD 23:59:59');
  } else if (opt === 'yesterday') {
    datetimeOutput.startDate = moment(new Date()).subtract(1, 'days').format('YYYY-MM-DD 00:00:00');
    datetimeOutput.endDate = moment(new Date()).subtract(1, 'days').format('YYYY-MM-DD 23:59:59');
  } else if (opt === 'last24') {
    const end = moment(new Date()).format('YYYY-MM-DD HH:mm:ss');
    datetimeOutput.startDate = moment(end).subtract(1, 'days').format('YYYY-MM-DD HH:mm:ss');
    datetimeOutput.endDate = end;
  }

  return datetimeOutput;
};

export default {
  name: 'WeatherSummary',
  data() {
    return {
      lineOptions,
      weatherSummary: {
        tempMin: null,
        tempMax: null,
        windAvg: null,
        windGustMax: null
      },
      measurements: [
        {
          measure: 'Temp (Min)',
          value: null
        },
        {
          measure: 'Temp (Max)',
          value: null
        },
        {
          measure: 'Wind (Avg)',
          value: null
        },
        {
          measure: 'Wind Gust (Max)',
          value: null
        }
      ],
      options: [
        {
          text: 'Irradiance',
          value: 'global_irradiance'
        },
        {
          text: 'Temperature',
          value: 'temperature'
        },
        {
          text: 'Wind',
          value: 'wind'
        }
      ],
      timeOpts: [
        {
          text: 'Today',
          value: 'today'
        },
        {
          text: 'Yesterday',
          value: 'yesterday'
        },
        {
          text: 'Last 24hrs',
          value: 'last24'
        }
      ],
      selectedOpt: 'global_irradiance',
      selectedTimeOpt: 'today'
    };
  },
  props: {
    gcu: {
      type: String
    }
  },
  methods: {
    async getData(gcuId, opt, timeOpt) {
      const datetime = formatDatetime(timeOpt);
      const opts = {
        gcuId,
        query: {
          startDate: datetime.startDate,
          endDate: datetime.endDate,
          temp: true,
          irrad: true,
          wind: true
        }
      };

      const data = await this.$store.dispatch('savanna/telemetry/getWeatherData', opts);
      const temp = data.map(a => a.temperature);
      const wind = data.map(a => a.wind_speed);
      const windGust = data.map(a => a.wind_gust);
      this.measurements[0].value = `${_.round(Math.min(...temp), 1)} °C`;
      this.measurements[1].value = `${_.round(Math.max(...temp), 1)} °C`;
      this.measurements[2].value = `${_.round(wind.reduce((a, b) => a + b, 0) / wind.length, 1)} m/s`;
      this.measurements[3].value = `${_.round(Math.max(...windGust), 1)} m/s`;

      const seriesBuffer = [];

      if (opt === 'wind') {
        lineOptions.yAxis.title.text = 'Wind [m/s]';
        lineOptions.title.text = 'Wind Data';
        seriesBuffer.push({
          name: 'Wind Speed',
          color: '#7cb5ec',
          data: data.map(a => [moment(a.date).valueOf(), a.wind_speed])
        });
        seriesBuffer.push({
          name: 'Wind Gust',
          color: '#434348',
          data: data.map(a => [moment(a.date).valueOf(), a.wind_gust])
        });
      } else if (opt === 'temperature') {
        lineOptions.yAxis.title.text = 'Temperature [°C]';
        lineOptions.title.text = 'Temperature Data';
        seriesBuffer.push({
          name: this.options.find(a => a.value === opt).text,
          color: '#7cb5ec',
          data: data.map(a => [moment(a.date).valueOf(), a.temperature])
        });
      } else if (opt === 'global_irradiance') {
        lineOptions.yAxis.title.text = 'Irradiance [W/m^2]';
        lineOptions.title.text = 'GHI Data';
        seriesBuffer.push({
          name: this.options.find(a => a.value === opt).text,
          color: '#7cb5ec',
          data: data.map(a => [moment(a.date).valueOf(), a.global_irradiance])
        });
      }

      lineOptions.series = seriesBuffer;
    }
  },
  watch: {
    gcu() {
      this.getData(this.gcu, this.selectedOpt, this.selectedTimeOpt);
    },
    selectedOpt() {
      this.getData(this.gcu, this.selectedOpt, this.selectedTimeOpt);
    },
    selectedTimeOpt() {
      this.getData(this.gcu, this.selectedOpt, this.selectedTimeOpt);
    }
  }
};
</script>

<style>
  #timeOpt > .btn.active,
  #displayOpt > .btn.active {
    z-index: 0;
  }

  #timeOpt,
  #displayOpt {
    width: 100%;
  }
</style>
